<template>
  <header class="ui-navbar">
    <div class="ui-navbar__inner">
      <div class="ui-navbar__logo">
        <router-link :to="{ name: 'Home' }" class="ui-navbar__logo-link">
          <span class="ui-navbar__logo-mark">
            indic<span class="ui-navbar__logo-oo">oo</span>
          </span>
        </router-link>
        <NavBadge />
      </div>

      <nav class="ui-navbar__links">
        <router-link :to="{ name: 'JobsList' }" class="ui-navbar__link">
          Vagas
        </router-link>
        <template v-for="link in links">
          <router-link v-if="link.to" :key="link.label" :to="link.to" class="ui-navbar__link">
            {{ link.label }}
          </router-link>
          <a v-else :key="link.label" :href="link.href" class="ui-navbar__link">
            {{ link.label }}
          </a>
        </template>
      </nav>

      <div class="ui-navbar__cta">
        <template v-if="isLogged">
          <router-link :to="{ name: dashboardRouteName }" class="ui-navbar__user">
            {{ currentUser.name }}
          </router-link>
          <BaseButton variant="ghost" size="sm" @click="handleLogout">
            Sair
          </BaseButton>
        </template>
        <template v-else>
          <BaseButton variant="ghost" size="sm" @click="$emit('login-click')">
            {{ loginLabel }}
          </BaseButton>
          <BaseButton variant="primary" size="sm" @click="$emit('start-click')">
            {{ startLabel }}
          </BaseButton>
        </template>
        <ThemeToggle />
      </div>
    </div>
  </header>

  <router-link :to="{ name: 'Home' }" class="ui-navbar__mobile-logo" aria-label="Indicoo, página inicial">
    <span class="ui-navbar__logo-mark">
      indic<span class="ui-navbar__logo-oo">oo</span>
    </span>
  </router-link>

  <button
    type="button"
    class="ui-navbar__burger"
    aria-label="Abrir menu"
    :aria-expanded="drawerOpen"
    @click="drawerOpen = true"
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  </button>

  <Teleport to="body">
    <Transition name="ui-navbar-backdrop">
      <div v-if="drawerOpen" class="ui-navbar__backdrop" @click="drawerOpen = false" />
    </Transition>
    <Transition name="ui-navbar-drawer">
      <aside v-if="drawerOpen" class="ui-navbar__drawer">
        <div class="ui-navbar__drawer-header">
          <span class="ui-navbar__logo-mark">
            indic<span class="ui-navbar__logo-oo">oo</span>
          </span>
          <button type="button" class="ui-navbar__drawer-close" aria-label="Fechar menu" @click="drawerOpen = false">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <nav class="ui-navbar__drawer-links">
          <router-link :to="{ name: 'JobsList' }" class="ui-navbar__drawer-link" @click="drawerOpen = false">
            Vagas
          </router-link>
          <template v-for="link in links">
            <router-link
              v-if="link.to"
              :key="link.label"
              :to="link.to"
              class="ui-navbar__drawer-link"
              @click="drawerOpen = false"
            >
              {{ link.label }}
            </router-link>
            <a
              v-else
              :key="link.label"
              :href="link.href"
              class="ui-navbar__drawer-link"
              @click="drawerOpen = false"
            >
              {{ link.label }}
            </a>
          </template>
        </nav>

        <div class="ui-navbar__drawer-footer">
          <template v-if="isLogged">
            <router-link
              :to="{ name: dashboardRouteName }"
              class="ui-navbar__user"
              @click="drawerOpen = false"
            >
              {{ currentUser.name }}
            </router-link>
            <BaseButton variant="ghost" size="sm" @click="handleLogoutDrawer">
              Sair
            </BaseButton>
          </template>
          <template v-else>
            <BaseButton variant="ghost" size="sm" @click="handleLoginDrawer">
              {{ loginLabel }}
            </BaseButton>
            <BaseButton variant="primary" size="sm" @click="handleStartDrawer">
              {{ startLabel }}
            </BaseButton>
          </template>
          <ThemeToggle />
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup>
import {
  computed, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import BaseButton from './BaseButton.vue';
import NavBadge from './NavBadge.vue';
import ThemeToggle from './ThemeToggle.vue';

defineProps({
  links: {
    type: Array,
    default: () => [
      { label: 'Como Funciona', to: { name: 'HowItsWorks' } },
      { label: 'Ganhos', to: { name: 'Home', hash: '#ganhos' } },
      { label: 'Depoimentos', to: { name: 'Home', hash: '#depoimentos' } },
    ],
  },
  loginLabel: {
    type: String,
    default: 'Entrar',
  },
  startLabel: {
    type: String,
    default: 'Começar a indicar →',
  },
});

const emit = defineEmits(['login-click', 'start-click']);

const store = useStore();
const router = useRouter();

// Called here (not just by the parent page) so AppNavbar reflects the
// logged-in state correctly no matter which page mounts it.
onMounted(() => {
  store.dispatch('validateLogged');
});

const isLogged = computed(() => store.state.user.isLogged);
const currentUser = computed(() => store.state.user.currentUser);
const dashboardRouteName = computed(() => {
  if (currentUser.value.isAdmin) return 'AdminDash';
  if (currentUser.value.isCompany) return 'CompanyDashboard';
  return 'UserDashboard';
});

const handleLogout = async () => {
  await store.dispatch('logout');
  await store.dispatch('validateLogged');
  router.push({ name: 'Home' });
};

const drawerOpen = ref(false);

const handleLoginDrawer = () => {
  drawerOpen.value = false;
  emit('login-click');
};

const handleStartDrawer = () => {
  drawerOpen.value = false;
  emit('start-click');
};

const handleLogoutDrawer = async () => {
  drawerOpen.value = false;
  await handleLogout();
};

const handleEscape = (event) => {
  if (event.key === 'Escape') drawerOpen.value = false;
};

watch(drawerOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : '';
  if (open) {
    window.addEventListener('keydown', handleEscape);
  } else {
    window.removeEventListener('keydown', handleEscape);
  }
});

onBeforeUnmount(() => {
  document.body.style.overflow = '';
  window.removeEventListener('keydown', handleEscape);
});
</script>

<style scoped>
.ui-navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  height: 68px;
  padding: 0 40px;
  background: var(--navy-deep);
  background: color-mix(in srgb, var(--navy-deep) 88%, transparent);
  backdrop-filter: blur(18px);
  border-bottom: 1px solid var(--navy-border);
}

.ui-navbar__inner {
  max-width: 1120px;
  height: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ui-navbar__logo {
  display: flex;
  align-items: center;
  gap: 14px;
}

.ui-navbar__logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.ui-navbar__logo-mark {
  font-family: var(--font-brand);
  font-size: 23px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: var(--white);
}

.ui-navbar__logo-oo {
  color: var(--cyan);
}

.ui-navbar__links {
  display: flex;
  align-items: center;
  gap: 32px;
}

.ui-navbar__link {
  font-family: var(--font-brand);
  font-size: 14px;
  font-weight: 500;
  color: var(--muted);
  text-decoration: none;
  transition: color 0.2s;
}

.ui-navbar__link:hover {
  color: var(--white);
}

.ui-navbar__cta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ui-navbar__user {
  font-family: var(--font-brand);
  font-size: 14px;
  font-weight: 600;
  color: var(--white);
  text-decoration: none;
  white-space: nowrap;
}

.ui-navbar__user:hover {
  color: var(--cyan);
}

.ui-navbar__burger {
  display: none;
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 999;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: var(--navy-card);
  border: 1px solid var(--navy-border);
  border-radius: var(--r-sm);
  color: var(--white);
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.ui-navbar__burger svg {
  width: 20px;
  height: 20px;
}

.ui-navbar__mobile-logo {
  display: none;
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 999;
  align-items: center;
  height: 44px;
  padding: 0 16px;
  background: var(--navy-card);
  border: 1px solid var(--navy-border);
  border-radius: var(--r-sm);
  text-decoration: none;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

@media (max-width: 900px) {
  .ui-navbar {
    display: none;
  }

  .ui-navbar__burger {
    display: flex;
  }

  .ui-navbar__mobile-logo {
    display: flex;
  }
}
</style>

<style>
.ui-navbar__backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.55);
}

.ui-navbar-backdrop-enter-active,
.ui-navbar-backdrop-leave-active {
  transition: opacity 0.25s ease;
}

.ui-navbar-backdrop-enter-from,
.ui-navbar-backdrop-leave-to {
  opacity: 0;
}

.ui-navbar__drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1001;
  width: min(320px, 85vw);
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: var(--navy-card);
  border-left: 1px solid var(--navy-border);
  box-shadow: -24px 0 64px rgba(0, 0, 0, 0.35);
}

.ui-navbar-drawer-enter-active,
.ui-navbar-drawer-leave-active {
  transition: transform 0.3s var(--ease);
}

.ui-navbar-drawer-enter-from,
.ui-navbar-drawer-leave-to {
  transform: translateX(100%);
}

.ui-navbar__drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--navy-border);
}

.ui-navbar__drawer-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--navy-border);
  border: none;
  border-radius: var(--r-sm);
  color: var(--white);
  cursor: pointer;
}

.ui-navbar__drawer-close svg {
  width: 16px;
  height: 16px;
}

.ui-navbar__drawer-links {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ui-navbar__drawer-link {
  padding: 12px 8px;
  font-family: var(--font-brand);
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
  text-decoration: none;
  border-radius: var(--r-sm);
}

.ui-navbar__drawer-link:hover {
  background: var(--navy-mid);
  color: var(--white);
}

.ui-navbar__drawer-footer {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid var(--navy-border);
}

.ui-navbar__drawer-footer .ui-navbar__user {
  padding: 8px;
}
</style>
